/*
 * Déclencheurs des changements d'étape du pipeline (CRM → Paramètres → Déclencheurs).
 * Pour chaque étape d'arrivée : le statut d'attente posé et les actions proposées
 * (contrat, messages). Les actions activées apparaissent cochées dans la fenêtre de passage d'étape,
 * où elles peuvent être décochées pour ce contact. Stockage : crm_settings, clé « triggers ».
 */
import { supabase } from '../../lib/supabase';
import type { Stage, WaitingOn } from './types';

export type TriggerStage = Exclude<Stage, 'lead'>;
export type TriggerType = 'send_contract' | 'send_message' | 'send_welcome' | 'send_invitation';

export interface TriggerAction {
  id: string;
  type: TriggerType;
  enabled: boolean;
  /** Message libre (send_message) : nom affiché, objet et texte (variables du CRM). */
  label?: string;
  subject?: string;
  body?: string;
}

export interface StageTriggers {
  /** Statut d'attente posé au passage à l'étape (null : aucun). */
  waiting: WaitingOn | null;
  actions: TriggerAction[];
}

export type TriggerSettings = Record<TriggerStage, StageTriggers>;

export const TRIGGER_STAGES: TriggerStage[] = ['contract_sent', 'contract_signed', 'sibelga', 'active'];

/** Actions intégrées : une seule par étape, non supprimables (activables ou non). */
export const TRIGGER_TYPES: Record<Exclude<TriggerType, 'send_message'>, { label: string; description: string; stage: TriggerStage }> = {
  send_contract: {
    label: 'Envoyer le contrat PDF',
    description: 'Contrat rempli (modèle selon le type de membre) joint au message « Envoi du contrat ».',
    stage: 'contract_sent',
  },
  send_welcome: {
    label: 'Envoyer le message de bienvenue (onboarding)',
    description: 'Message « Message de bienvenue ». Le voyant Onboarding passe au vert.',
    stage: 'active',
  },
  send_invitation: {
    label: 'Inviter à créer un compte sur la plateforme',
    description: 'Message « Invitation à la plateforme », envoyé après la création des fiches participants.',
    stage: 'active',
  },
};

const message = (id: string, label: string, subject: string, body: string): TriggerAction =>
  ({ id, type: 'send_message', enabled: false, label, subject, body });

export const DEFAULT_TRIGGERS: TriggerSettings = {
  contract_sent: {
    waiting: 'client',
    actions: [{ id: 'contract', type: 'send_contract', enabled: true }],
  },
  contract_signed: {
    waiting: 'sunisup',
    actions: [message('contract-received', 'Accusé de réception du contrat',
      'Nous avons bien reçu votre contrat – Sun Is Up',
      'Bonjour,\n\nNous avons bien reçu votre contrat signé. Nous préparons maintenant l’inscription de votre compteur auprès de Sibelga.\n\nBien cordialement,\nL’équipe Sun Is Up')],
  },
  sibelga: {
    waiting: null,
    actions: [message('sibelga-submitted', 'Dossier transmis à Sibelga',
      'Votre dossier est transmis à Sibelga – Sun Is Up',
      'Bonjour,\n\nVotre dossier a été transmis à Sibelga pour l’opération {{operation}}. Le partage d’énergie commencera dès que Sibelga aura validé l’inscription de votre compteur.\n\nBien cordialement,\nL’équipe Sun Is Up')],
  },
  active: {
    waiting: null,
    actions: [
      { id: 'welcome', type: 'send_welcome', enabled: false },
      { id: 'invitation', type: 'send_invitation', enabled: false },
      message('sharing-start', 'Début du partage',
        'Votre partage d’énergie commence le {{date_debut}}',
        'Bonjour,\n\nVotre participation au partage d’énergie commence le {{date_debut}}, au prix de {{prix}} c€/kWh HTVA.\n\nBien cordialement,\nL’équipe Sun Is Up'),
    ],
  },
};

const WAITING_VALUES: (WaitingOn | null)[] = ['client', 'sunisup', null];

/** Complète des réglages enregistrés avec les valeurs par défaut (actions intégrées toujours présentes). */
export function normalizeTriggers(value: Partial<Record<TriggerStage, Partial<StageTriggers>>> | null | undefined): TriggerSettings {
  const out = {} as TriggerSettings;
  for (const stage of TRIGGER_STAGES) {
    const saved = value?.[stage];
    const defaults = DEFAULT_TRIGGERS[stage];
    const savedActions = Array.isArray(saved?.actions) ? saved!.actions.filter(a => a && typeof a.id === 'string' && a.type) : null;
    let actions: TriggerAction[] = savedActions ? savedActions.map(a => ({ ...a, enabled: !!a.enabled })) : defaults.actions.map(a => ({ ...a }));
    // Les actions intégrées de l'étape ne disparaissent jamais ; celles d'une autre étape sont ignorées.
    actions = actions.filter(a => a.type === 'send_message' || TRIGGER_TYPES[a.type]?.stage === stage);
    for (const d of defaults.actions) {
      if (d.type !== 'send_message' && !actions.some(a => a.type === d.type)) actions.unshift({ ...d });
    }
    out[stage] = {
      waiting: saved && WAITING_VALUES.includes(saved.waiting ?? null) ? (saved.waiting ?? null) : defaults.waiting,
      actions,
    };
  }
  return out;
}

let cache: TriggerSettings | null = null;

export async function fetchTriggerSettings(force = false): Promise<TriggerSettings> {
  if (cache && !force) return cache;
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', 'triggers').maybeSingle();
  if (error) throw error;
  cache = normalizeTriggers((data as { value?: Partial<Record<TriggerStage, Partial<StageTriggers>>> } | null)?.value);
  return cache;
}

/** Champs manquants d'un message (nom, objet, texte) ; vide si complet. */
export function missingMessageFields(a: TriggerAction): ('label' | 'subject' | 'body')[] {
  if (a.type !== 'send_message') return [];
  return (['label', 'subject', 'body'] as const).filter(k => !a[k]?.trim());
}

/** Premier message incomplet : étape et action, pour l'ouvrir et le signaler. */
export function firstIncompleteMessage(value: TriggerSettings): { stage: TriggerStage; action: TriggerAction } | null {
  for (const stage of TRIGGER_STAGES) {
    const action = value[stage].actions.find(a => missingMessageFields(a).length > 0);
    if (action) return { stage, action };
  }
  return null;
}

export async function saveTriggerSettings(value: TriggerSettings): Promise<void> {
  const incomplete = firstIncompleteMessage(value);
  if (incomplete) {
    const names = { label: 'le nom', subject: 'l’objet', body: 'le texte' };
    const missing = missingMessageFields(incomplete.action).map(k => names[k]).join(', ');
    throw new Error(`Message « ${incomplete.action.label?.trim() || 'sans nom'} » : complétez ${missing}`);
  }
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: 'triggers', value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
  cache = normalizeTriggers(value);
}

export const actionLabel = (a: TriggerAction) => (a.type === 'send_message' ? a.label?.trim() || 'Message' : TRIGGER_TYPES[a.type].label);

export const newMessageAction = (): TriggerAction => ({
  id: `msg-${Date.now().toString(36)}`, type: 'send_message', enabled: true, label: 'Nouveau message', subject: '', body: 'Bonjour,\n\n\n\nBien cordialement,\nL’équipe Sun Is Up',
});
